import { useMemo } from "react";
import { TranslationKey } from "@typedefs/modulesInterfaces";
import TranslatableText from "@components/translations/TranslatableText";
import Skeleton from "react-loading-skeleton";

interface Props {
	country?: string;
	climate?: string;
	moisture?: string;
	soilType?: string;
	implDuration?: number;
	capDuration?: number;
	isLoading?: boolean;
	totalHectares?: number | null;
	totalCatch?: number | null;
	totalLivestock?: number | null;
}

interface DataItem {
	label: TranslationKey;
	value: string | number | null | undefined;
}

const DescriptionBlock = ({
	country,
	climate,
	moisture,
	soilType,
	implDuration,
	capDuration,
	isLoading,
	totalHectares,
	totalCatch,
	totalLivestock,
}: Props) => {
	const columnOneData: DataItem[] = useMemo(() => {
		const baseData: DataItem[] = [
			{ label: "main.country", value: country },
			{ label: "main.climate", value: climate },
			{ label: "main.moisture", value: moisture },
			{ label: "main.soil_type", value: soilType },
		];

		const optionalData: DataItem[] = [
			...(totalHectares ? [{ label: "main.total_hectares" as TranslationKey, value: totalHectares }] : []),
			...(totalCatch ? [{ label: "main.total_catch" as TranslationKey, value: totalCatch }] : []),
			...(totalLivestock ? [{ label: "main.total_livestock" as TranslationKey, value: totalLivestock }] : []),
		];

		return [...baseData, ...optionalData];
	}, [country, climate, moisture, soilType, totalHectares, totalCatch, totalLivestock]);

	const columnTwoData: DataItem[] = useMemo(() => [
		{ label: "main.implementation_phase", value: implDuration },
		{ label: "main.capitalization_phase", value: capDuration },
	], [implDuration, capDuration]);

	const totalDuration = useMemo(() => {
		return (implDuration ?? 0) + (capDuration ?? 0);
	}, [implDuration, capDuration]);

	const renderValueSpan = (value: string | number | null | undefined) => (
		<span className="capitalize ff-bold">
			{value ?? ""}
		</span>
	);

	const renderSkeletons = (count: number, width: number = 100) => (
		Array.from({ length: count }, (_, index) => (
			<Skeleton key={index} width={width} height={14} />
		))
	);

	return (
		<>
			{/* Column 1: Labels */}
			<div
				style={{ gridColumn: 1, gridRow: 1 }}
				className="d-flex f-column justify-content-around"
			>
				{columnOneData.map(({ label }) => (
					<TranslatableText suffix=":" key={label} translationKey={label} />
				))}
			</div>

			{/* Column 2: Values */}
			<div
				className="d-flex f-column pe-2 justify-content-around"
				style={{ gridColumn: 2, gridRow: 1 }}
			>
				{isLoading ? (
					renderSkeletons(columnOneData.length)
				) : (
					columnOneData.map(({ label, value }) => (
						<div key={label}>
							{renderValueSpan(value)}
						</div>
					))
				)}
			</div>

			{/* Column 3: Duration Labels */}
			<div
				className="d-flex f-column justify-content-between"
				style={{ gridColumn: 3, gridRow: 1 }}
			>
				<div className="d-flex f-column pe-1">
					{columnTwoData.map(({ label }) => (
						<TranslatableText suffix=":" key={label} translationKey={label} />
					))}
				</div>
				<div style={{ maxWidth: "200px" }}>
					<TranslatableText 
						suffix=":" 
						className="text-wrap" 
						style={{ display: "inline", whiteSpace: "normal" }} 
						translationKey="main.total_duration_of_accounting" 
					/>
				</div>
			</div>

			{/* Column 4: Duration Values */}
			<div
				className="d-flex f-column justify-content-between"
				style={{ gridColumn: 4, gridRow: 1 }}
			>
				<div className="d-flex f-column pe-1">
					{isLoading ? (
						renderSkeletons(columnTwoData.length, 40)
					) : (
						columnTwoData.map(({ label, value }) => (
							<div key={label}>
								{renderValueSpan(value)}
							</div>
						))
					)}
				</div>
				{isLoading ? (
					<Skeleton width={40} height={14} />
				) : (
					renderValueSpan(totalDuration)
				)}
			</div>
		</>
	);
};

export default DescriptionBlock;
